import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import type { BloxCustomDataProviderType } from './types/dataProvider';
import type { Workflow } from 'providers/contexts/extensions/Workflows';

/**
 * Normalizes a workflow ID candidate string.
 * Returns null if the value is null, undefined, or empty/whitespace after trimming.
 */
export function normalizeWorkflowId(id: string | null | undefined): string | null {
  try {
    if (!id || typeof id !== 'string') {
      return null;
    }
    const trimmed = id.trim();
    if (!trimmed) {
      return null;
    }
    return trimmed;
  } catch (error) {
    console.error('[normalizeWorkflowId] Error normalizing workflow id:', error);
    return null;
  }
}

type UseValidatedDefaultWorkflowIdResult = {
  /** The workflow ID confirmed to exist in the system, or null if the ID is absent or not found. */
  effectiveWorkflowId: string | null;
  /** True while the API validation call is in-flight (only when a candidateId is provided). */
  isPending: boolean;
  /** True after validation resolves and confirms the candidate workflow does not exist. */
  isInvalidCandidate: boolean;
};

/**
 * Validates a workflow ID candidate against the API by fetching it by ID.
 * Returns the original candidate ID as effectiveWorkflowId if the workflow exists,
 * or null if the ID is empty, the workflow was not found, or the request failed.
 *
 * The query is shared via TanStack Query's cache, so multiple consumers using the
 * same candidateId will deduplicate the network call.
 */
export function useValidatedDefaultWorkflowId(
  candidateId: string | null,
): UseValidatedDefaultWorkflowIdResult {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const query = useQuery<Workflow | null>({
    queryKey: ['validatedDefaultWorkflowById', candidateId],
    queryFn: async (): Promise<Workflow | null> => {
      if (!candidateId) return null;
      try {
        const result = await dataProvider.getWorkflowById({
          query: { workflow_id: candidateId },
        });
        return result?.data ?? null;
      } catch (error) {
        return null;
      }
    },
    retry: false,
    enabled: Boolean(candidateId),
  });

  // Only return the candidate ID when the API has confirmed the workflow exists.
  const effectiveWorkflowId = candidateId && query.data?.id ? candidateId : null;

  // isPending is only meaningful when there is a candidate to validate.
  const isPending = Boolean(candidateId) && query.isPending;

  // Mark candidate invalid only after the query has resolved at least once.
  const isInvalidCandidate =
    Boolean(candidateId) && query.isFetched && !Boolean(query.data?.id);

  return { effectiveWorkflowId, isPending, isInvalidCandidate };
}
